import React from "react"
import { fabric } from "fabric"
import {
  baseImgOpts,
  placeholderBaseOpts,
  controls,
  CANVAS_SIZE,
  TEXT_SCALE,
} from "./../helpers/base-options"
import asyncLoadFabricImg, {
  asyncSetBgImg,
} from "./../helpers/async-load-fabric-img"

const asyncCloneCanvas = (canvas) =>
  new Promise((resolve) => canvas.cloneWithoutData(resolve))

/**
 *
 * @param canvas
 * @param props
 * @returns {{loading: *}}
 */
const useDesigner = (canvas, props) => {
  const { size } = props

  const [loading, setLoading] = React.useState(true)

  const getPreview = async (canvas, { src, logo, placeholderOpts = {} }) => {
    let obj = null
    const img = await asyncLoadFabricImg(src, baseImgOpts)

    img.scaleToHeight(CANVAS_SIZE)
    img.scaleToWidth(CANVAS_SIZE)

    await asyncSetBgImg(canvas, img)

    const uploadSection = new fabric.Rect(placeholderBaseOpts)

    const text = new fabric.Text(props.logoDimensions, {
      fill: "white",
      textAlign: "center",
      fontFamily: "Open Sans Condensed",
      left: uploadSection.width / 2,
      top: 0,
      scaleX: TEXT_SCALE,
      scaleY: TEXT_SCALE,
      ...controls,
    })

    text.set({
      top: uploadSection.height / 2 - (text.height * TEXT_SCALE) / 2,
      left: uploadSection.width / 2 - (text.width * TEXT_SCALE) / 2,
    })

    const placeholder = new fabric.Group([uploadSection, text], {
      ...placeholderOpts,
      ...controls,
    })

    /**
     * @desc Create canvas with logo image
     */
    if (logo) {
      obj = await asyncLoadFabricImg(logo, placeholderOpts, controls)

      obj.set({
        left: placeholder.left,
        top: placeholder.top,
        scaleX: placeholder.scaleX,
        scaleY: placeholder.scaleY,
        ...controls,
      })

      obj.scaleToHeight(placeholder.getScaledWidth())
      obj.scaleToWidth(placeholder.getScaledHeight())

      obj.setPositionByOrigin(placeholder.getCenterPoint(), "center", "center")
    } else {
      /**
       * @desc Create Rect
       */
      obj = placeholder
    }

    /**
     * @desc Add corresponding fabric object to canvas
     */
    canvas.add(obj)

    return logo ? canvas.toDataURL() : null
  }

  const init = async (canvas) => {
    const [thumb, original] = await Promise.all([
      asyncCloneCanvas(canvas).then((ctx) =>
        getPreview(ctx, {
          src: props.bgThumbnailSrc,
          logo: props.logoSrc,
          placeholderOpts: props.placeholderOpts,
          size,
        })
      ),
      getPreview(canvas, {
        src: props.bgSrc,
        logo: props.logoSrc,
        placeholderOpts: props.placeholderOpts,
        size,
      }),
    ])

    return {
      thumb, // image without dimensions
      original, // image with dimensions
    }
  }

  React.useLayoutEffect(() => {
    if (canvas) {
      canvas.on("after:render", () => {
        setLoading(false)
      })

      init(canvas)
        .then(props.onChangePreview) // update preview with relevant date
        .finally(canvas.renderAll.bind(canvas)) // render all layers at the ends

      return () => {
        canvas.clear()
      }
    }
  }, [canvas, props.bgSrc, props.logoSrc, size])

  return { loading, getPreview }
}

export default useDesigner
